import React, { useEffect, useState } from 'react';

import {
  EmployeeLicenseStatus,
  EmployeeLicenseWarning,
  EmployeeRole,
  States,
} from '@vestahealthcare/common/enums';
import Enum, { Selectable } from '@vestahealthcare/common/enums/Enum';
import { translate } from '@vestahealthcare/common/i18n';

import { Panel, Select, SelectLanguage, TextInput } from 'styleguide-v2';
import {
  FilterGroup,
  FilterItem,
  FilterSelectable,
} from 'styleguide-v2/src/components/Panel';
import { FilterEmployeeOptions } from 'styleguide-v2/src/components/SelectAssignee';

import { EmployeeParams } from 'dash/src/services/EmployeeServices';
import { getFilterCount, getFilterItem } from 'dash/src/utils/filterUtils';

type KeyGetEmployeeParams = keyof EmployeeParams;

type Props = {
  data: {
    employeeGroups: Selectable[];
    languages: Selectable[];
    leadEmployees: Selectable[];
    permissions: Selectable[];
  };
  disabledFields?: KeyGetEmployeeParams[];
  filters?: {
    [x in KeyGetEmployeeParams]?: FilterItem;
  };
  loadingFilters?: boolean;
  open: boolean;
  onChange: (
    filters: {
      [x in KeyGetEmployeeParams]?: FilterItem;
    },
  ) => void;
  onClose: () => void;
};

const EMPLOYEE_FILTERS: KeyGetEmployeeParams[] = [
  'employeeId',
  'searchName',
  'employeeGroupId',
  'role',
  'permission',
  'enabled',
  'phiAccess',
  'state',
];

const LICENSE_FILTERS: KeyGetEmployeeParams[] = ['state'];

export const EmployeeFiltersModal = ({
  data,
  disabledFields,
  filters,
  loadingFilters,
  open,
  onChange,
  onClose,
}: Props) => {
  const [pendingFilters, setPendingFilters] = useState<
    {
      [x in KeyGetEmployeeParams]?: FilterItem;
    }
  >(filters || {});

  useEffect(() => {
    setPendingFilters(filters || {});
  }, [filters]);

  return (
    <Panel.FilterModal
      open={open}
      onClear={() => {
        onChange({});
      }}
      onClose={onClose}
      onSubmit={() => onChange(pendingFilters)}
    >
      <FilterGroup
        title="Employee Details"
        count={getFilterCount(pendingFilters, EMPLOYEE_FILTERS)}
        open
      >
        <TextInput
          data-cy="employee-filter-modal-id"
          disabled={disabledFields?.includes('employeeId')}
          onChange={(value?: string) => {
            setPendingFilters({
              ...pendingFilters,
              employeeId: getFilterItem(
                translate('dashboardEvents.filters.id'),
                value,
              ),
            });
          }}
          label={translate('employees.filters.id')}
          size="small"
          value={
            (pendingFilters?.employeeId?.value as FilterSelectable)
              ?.value as string
          }
        />
        <TextInput
          data-cy="employee-filter-modal-name"
          disabled={disabledFields?.includes('searchName')}
          onChange={(value?: string) => {
            setPendingFilters({
              ...pendingFilters,
              searchName: getFilterItem(
                translate('employees.filters.name'),
                value,
              ),
            });
          }}
          label={translate('employees.filters.name')}
          size="small"
          value={
            (pendingFilters?.searchName?.value as FilterSelectable)
              ?.value as string
          }
        />
        <Select
          data-cy="employee-filter-referral-groups"
          disabled={disabledFields?.includes('employeeGroupId')}
          items={data.employeeGroups}
          label={translate('employees.filters.groups')}
          limitTags={1}
          loading={loadingFilters}
          multiple
          onChange={(values: Selectable[]) => {
            setPendingFilters({
              ...pendingFilters,
              employeeGroupId: getFilterItem(
                translate('employees.filters.groups'),
                values,
              ),
            });
          }}
          size="small"
          value={pendingFilters.employeeGroupId?.value || []}
        />
        <Select
          data-cy="employee-filter-referral-role"
          disabled={disabledFields?.includes('role')}
          items={EmployeeRole.toSelectable()}
          label={translate('employees.filters.role')}
          limitTags={1}
          loading={loadingFilters}
          multiple
          onChange={(values: Selectable[]) => {
            setPendingFilters({
              ...pendingFilters,
              role: getFilterItem(translate('employees.filters.role'), values),
            });
          }}
          size="small"
          value={pendingFilters.role?.value || []}
        />
        <Select
          data-cy="employee-filter-referral-permission"
          disabled={disabledFields?.includes('permission')}
          items={data.permissions}
          label={translate('employees.filters.permission')}
          limitTags={1}
          loading={loadingFilters}
          multiple
          onChange={(values: Selectable[]) => {
            setPendingFilters({
              ...pendingFilters,
              permission: getFilterItem(
                translate('employees.filters.permission'),
                values,
              ),
            });
          }}
          size="small"
          value={pendingFilters.permission?.value || []}
        />
        <SelectLanguage
          data-cy="employee-filter-modal-language"
          disabled={disabledFields?.includes('language')}
          items={data.languages}
          label={translate('employees.filters.language')}
          limitTags={1}
          loading={loadingFilters}
          onChange={(values: Selectable[]) => {
            setPendingFilters({
              ...pendingFilters,
              language: getFilterItem(
                translate('employees.filters.language'),
                values,
              ),
            });
          }}
          size="small"
          value={(pendingFilters.language?.value as Selectable[]) || []}
        />
        <Select
          data-cy="employee-filter-modal-lead"
          disabled={disabledFields?.includes('lead')}
          items={data.leadEmployees}
          filterOptions={FilterEmployeeOptions}
          label={translate('employees.filters.employeeLead')}
          limitTags={1}
          loading={loadingFilters}
          multiple
          onChange={(values: Selectable[]) => {
            setPendingFilters({
              ...pendingFilters,
              lead: getFilterItem(
                translate('employees.filters.employeeLead'),
                values,
              ),
            });
          }}
          size="small"
          value={pendingFilters.lead?.value || []}
        />
        <Select.Choice
          data-cy="employee-filter-modal-active"
          disabled={disabledFields?.includes('enabled')}
          label={translate('employees.filters.active')}
          onChange={(value?: boolean) => {
            setPendingFilters({
              ...pendingFilters,
              enabled: getFilterItem(
                translate('employees.filters.active'),
                value,
              ),
            });
          }}
          size="small"
          value={
            (filters?.enabled?.value as FilterSelectable)?.value as
              | boolean
              | undefined
          }
        />
        <Select.Choice
          data-cy="employee-filter-modal-phiAccess"
          disabled={disabledFields?.includes('phiAccess')}
          label={translate('employees.filters.phiAccess')}
          onChange={(value?: boolean) => {
            setPendingFilters({
              ...pendingFilters,
              phiAccess: getFilterItem(
                translate('employees.filters.phiAccess'),
                value,
              ),
            });
          }}
          size="small"
          value={
            (filters?.phiAccess?.value as FilterSelectable)?.value as
              | boolean
              | undefined
          }
        />
      </FilterGroup>
      <FilterGroup
        title="License Details"
        count={getFilterCount(pendingFilters, LICENSE_FILTERS)}
        open
      >
        <Select
          data-cy="employee-filter-modal-license-state"
          disabled={disabledFields?.includes('state')}
          items={Enum.toSelectable(States.asArray)}
          label={translate('employees.filters.state')}
          multiple
          onChange={(values?: Selectable[]) => {
            setPendingFilters({
              ...pendingFilters,
              state: getFilterItem(
                translate('employees.filters.state'),
                values,
              ),
            });
          }}
          size="small"
          value={filters?.state?.value}
        />
        <Select
          data-cy="employee-filter-modal-license-status"
          disabled={disabledFields?.includes('licenseStatus')}
          items={Enum.toSelectable(EmployeeLicenseStatus.asArray)}
          label={translate('employees.filters.licenseStatus')}
          multiple
          onChange={(values?: Selectable[]) => {
            setPendingFilters({
              ...pendingFilters,
              licenseStatus: getFilterItem(
                translate('employees.filters.licenseStatus'),
                values,
              ),
            });
          }}
          size="small"
          value={filters?.licenseStatus?.value}
        />
        <Select
          data-cy="employee-filter-modal-license-warning"
          disabled={disabledFields?.includes('licenseWarning')}
          items={Enum.toSelectable(EmployeeLicenseWarning.asArray)}
          label={translate('employees.filters.licenseWarning')}
          onChange={(values?: Selectable[]) => {
            setPendingFilters({
              ...pendingFilters,
              licenseWarning: getFilterItem(
                translate('employees.filters.licenseWarning'),
                values,
              ),
            });
          }}
          size="small"
          value={filters?.licenseWarning?.value}
        />
      </FilterGroup>
    </Panel.FilterModal>
  );
};
